import React, { useCallback, useEffect, useRef, useState } from "react";

/**
 * Embedded Signup do WhatsApp (coexistência com o app WhatsApp Business).
 * Render-props: render({ onClick, isProcessing }).
 * callback recebe { status, code, waba_id, phone_number_id, business_id }.
 * Não registra o número via API (/register) — o app do celular continua ativo.
 */
const SDK_ID = "facebook-jssdk";
const API_VERSION = process.env.REACT_APP_FACEBOOK_API_VERSION || "v22.0";

let sdkPromise = null;

function loadFacebookSdk(appId) {
  if (sdkPromise) return sdkPromise;
  sdkPromise = new Promise((resolve, reject) => {
    const init = () => {
      window.FB.init({ appId, cookie: true, xfbml: false, version: API_VERSION });
      resolve(window.FB);
    };
    if (window.FB) {
      init();
      return;
    }
    window.fbAsyncInit = init;
    if (!document.getElementById(SDK_ID)) {
      const js = document.createElement("script");
      js.id = SDK_ID;
      js.async = true;
      js.defer = true;
      js.src = "https://connect.facebook.net/pt_BR/sdk.js";
      js.onerror = () => {
        sdkPromise = null;
        reject(new Error("Não foi possível carregar o SDK do Facebook"));
      };
      document.body.appendChild(js);
    }
  });
  return sdkPromise;
}

const WhatsAppEmbeddedSignup = ({ appId, configId, callback, render, isDisabled = false }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const sessionRef = useRef(null);

  useEffect(() => {
    if (appId) loadFacebookSdk(appId).catch(() => {});
  }, [appId]);

  useEffect(() => {
    const onMessage = (event) => {
      if (!event.origin || !/facebook\.com$/.test(new URL(event.origin).hostname)) return;
      try {
        const data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
        if (data && data.type === "WA_EMBEDDED_SIGNUP") {
          sessionRef.current = data;
        }
      } catch (e) {
        /* mensagens não-JSON do SDK */
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  const finish = useCallback(
    (response) => {
      const code = response && response.authResponse && response.authResponse.code;
      if (!code) {
        setIsProcessing(false);
        callback({ status: "unknown" });
        return;
      }
      // O evento da sessão (waba/phone) pode chegar logo depois do callback do login.
      setTimeout(() => {
        const s = sessionRef.current;
        const d = (s && s.data) || {};
        setIsProcessing(false);
        callback({
          status: "connected",
          code,
          waba_id: d.waba_id,
          phone_number_id: d.phone_number_id,
          business_id: d.business_id,
          event: s && s.event
        });
      }, 1500);
    },
    [callback]
  );

  const onClick = useCallback(
    (e) => {
      if (e && e.preventDefault) e.preventDefault();
      if (isDisabled || isProcessing || !configId) return;
      sessionRef.current = null;

      const options = {
        config_id: configId,
        response_type: "code",
        override_default_response_type: true,
        extras: {
          setup: {},
          featureType: "whatsapp_business_app_onboarding",
          sessionInfoVersion: "3"
        }
      };

      if (window.FB) {
        setIsProcessing(true);
        window.FB.login(finish, options);
        return;
      }
      setIsProcessing(true);
      loadFacebookSdk(appId)
        .then((FB) => FB.login(finish, options))
        .catch(() => {
          setIsProcessing(false);
          callback({ status: "unknown" });
        });
    },
    [appId, configId, finish, callback, isDisabled, isProcessing]
  );

  return render({ onClick, isProcessing, isDisabled });
};

export default WhatsAppEmbeddedSignup;
